import type {
  ConditionGroupMap,
  ConditionMap
} from "../components/RulesUI/Constraints/constraints.types";
import { getRulesModel } from "./rules.model";
import { uuidWithProcessId } from "../utils/db.utils";
import { cleanRawRules } from "../utils/ruleCleaning.utils";
import { NON_FILTERABLE_FIELDS } from "../constants/field.constants";
import { ENV_REGEX } from "../constants/regex.constants";
import { FieldType } from "@certa/types";
import type { Field, RuleMap } from "@certa/studio-core";
import { RuleType } from "@certa/studio-core";
import type { ZodField } from "@certa/queries/schemas/studio/deployableJSON.schema";
import type { ZodFieldAppJSON } from "@certa/queries/schemas/studio/appProcessJSON.schema";
import { removeStudioParams } from "../utils/general";

/**
 * TODO:
 * - Handle unhandled props
 * - Handle Data inheritance migration from v1 to v2
 */

const constraintRuleTypeMapping: Record<string, RuleType> = {
  RAGConstraints: RuleType.RAG,
  attachmentInheritanceConstraints: RuleType.AttachmentInheritance,
  attachmentInheritanceConstraintsV2: RuleType.AttachmentInheritanceV2,
  childWorkflowCreationConstraints: RuleType.ChildProcessCreation,
  dataInheritanceConstraints: RuleType.DataInheritance,
  dataReductionConstraints: RuleType.DataReduction,
  integrationPollingConstraints: RuleType.IntegrationPolling,
  integrationTriggerConstraints: RuleType.IntegrationTrigger,
  relatedWorkflowFieldConstraints: RuleType.RelatedProcessDataInheritance,
  visibilityConstraints: RuleType.FieldVisibility,
  relatedWorkflowFileConstraintsV2: RuleType.RelatedWorkflowFileConstraintsV2,
  readOnlyFieldConstraints: RuleType.ReadOnlyFieldConstraints
};

const constraintRawPropMapping = {
  RAGConstraints: "status_constraints",
  attachmentInheritanceConstraints: "attachment_inheritance_constraints",
  attachmentInheritanceConstraintsV2: "attachment_inheritance_constraints_v2",
  childWorkflowCreationConstraints: "child_workflow_creation_constraints",
  dataInheritanceConstraints: "inheritance_constraints_v2",
  dataReductionConstraints: "reduction_constraints",
  integrationPollingConstraints: "integration_polling_constraints",
  integrationTriggerConstraints: "integration_trigger_constraints",
  relatedWorkflowFieldConstraints: "related_workflow_field_constraints",
  visibilityConstraints: "constraints",
  relatedWorkflowFileConstraintsV2: "related_workflow_file_constraints_v2",
  readOnlyFieldConstraints: "read_only_field_constraints"
} as const;

const fieldConstraintKeys = [
  "RAGConstraints",
  "attachmentInheritanceConstraints",
  "attachmentInheritanceConstraintsV2",
  "childWorkflowCreationConstraints",
  "dataInheritanceConstraints",
  "dataReductionConstraints",
  "integrationPollingConstraints",
  "integrationTriggerConstraints",
  "relatedWorkflowFieldConstraints",
  "visibilityConstraints",
  "relatedWorkflowFileConstraintsV2",
  "readOnlyFieldConstraints"
] as const;

const fieldConstraintKeysRaw: string[] = Object.values(
  constraintRawPropMapping
);

export const fieldModelCreator = (
  rawField: ZodField | ZodFieldAppJSON,
  { processId }: { processId: string }
) => {
  const {
    tag,
    body,
    jmes_path: jmesPath,
    data_json_path: dataJSONPath,
    source_csv_json: {
      csv_source: csvSource,
      filter_columns: filterColumns,
      ...restSourceCSVJSON
    } = {},
    default_threads: defaultThreads,
    default_value: defaultValue,
    field_type: fieldType,
    disabled: isDisabled,
    external_tag: externalTag,
    ext_is_required: isExtIsRequired,
    extra,
    meta,
    help_text: helpText,
    hidden: isHidden,
    is_encrypted: isEncrypted,
    is_enabled: isEnabled,
    is_filterable: isFilterable,
    is_required: isRequired,
    log_on_change: shouldLogOnChange,
    options,
    prasaran_on_change: hasPrasaranOnChange,
    parent_json_field: parentJSONField,
    regex_error: regexError,
    regex_value: regexValue,
    required_on_step_submit: isRequiredOnStepSubmit,
    search_param_json: searchParamJson,
    visible_cw_activity_log: shouldVisibleCWActivityLog,
    size,
    source_mapping: sourceMapping,
    attachment_url: attachmentUrl,
    workflow_mapping: workflowMapping,
    model_field_name: modelFieldName,
    status_indicators: statusIndicators,
    child_workflow_creation_constraints: childWorkflowCreationConstraintsRaw,
    __cs_comment: __csComment,
    ...restRawField
  } = rawField;

  const sanitizedRestRawField = removeStudioParams(restRawField);
  // restWithoutConstraints will remain unknown
  const restWithoutConstraints = Object.entries(sanitizedRestRawField).reduce(
    (acc: Record<string, unknown>, [key, value]) => {
      // remove remaining known properties
      if (!fieldConstraintKeysRaw.includes(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );

  const field: Field = {
    ...restWithoutConstraints,
    id:
      ("__studio_id" in rawField
        ? rawField.__studio_id
        : uuidWithProcessId(processId)) ?? uuidWithProcessId(processId),
    tag,
    body, // TODO: handle INTL values
    jmesPath,
    dataJSONPath,
    sourceCSVJSON:
      rawField.source_csv_json &&
      Object.values(rawField.source_csv_json).length > 0
        ? {
            ...restSourceCSVJSON,
            csvSource: csvSource,
            filterColumns: filterColumns
          }
        : undefined,
    defaultThreads,
    defaultValue: ENV_REGEX.test(defaultValue || "")
      ? ![FieldType.BUSINESS_UNIT, FieldType.REGION].includes(
          fieldType as FieldType
        )
        ? defaultValue
        : undefined
      : [
            FieldType.DROPDOWN_MULTI,
            FieldType.CHECKBOX,
            FieldType.GROUPED_CHECKBOX
          ].includes(fieldType as FieldType)
        ? defaultValue?.split("~")
        : defaultValue,
    envOrSysVariable:
      ENV_REGEX.test(defaultValue || "") &&
      [FieldType.BUSINESS_UNIT, FieldType.REGION].includes(
        fieldType as FieldType
      )
        ? defaultValue
        : undefined,
    disabled: isDisabled,
    externalTag,
    extIsRequired: isExtIsRequired,
    extra: fieldExtraModelCreator(extra),
    fieldType,
    meta:
      meta?.height !== undefined
        ? {
            height: meta?.height
          }
        : undefined,
    helpText,
    hidden: isHidden,
    isEncrypted,
    isEnabled,
    isFilterable:
      !NON_FILTERABLE_FIELDS.includes(fieldType) && isFilterable === undefined
        ? !isHidden
          ? true
          : undefined
        : isFilterable,
    isRequired,
    logOnChange: shouldLogOnChange,
    options,
    prasaranOnChange: hasPrasaranOnChange,
    parentJSONField,
    regexError,
    regexValue,
    requiredOnStepSubmit: isRequiredOnStepSubmit,
    searchParamJson,
    visibleCWActivityLog: shouldVisibleCWActivityLog,
    size,
    sourceMapping,
    attachmentUrl,
    workflowMapping,
    modelFieldName,
    statusIndicators,
    childWorkflowCreationConstraintsRaw,
    __cs_comment: __csComment,
    __createdAt:
      ("__studio_created_at" in rawField
        ? rawField.__studio_created_at
        : Date.now()) ?? Date.now(),
    __modifiedAt:
      ("__studio_modified_at" in rawField
        ? rawField.__studio_modified_at
        : Date.now()) ?? Date.now(),
    __tagReadonly: rawField.tag ? true : false
  };

  let aggregatedRuleMap: RuleMap = {};
  let aggregatedConditionGroupMap: ConditionGroupMap = {};
  let aggregatedConditionMap: ConditionMap = {};

  // Now we parse all constraints and populate them accordingly.
  fieldConstraintKeys.forEach(constraintKey => {
    if (!rawField[constraintRawPropMapping[constraintKey]]) {
      return;
    }
    const rawRules = cleanRawRules(
      rawField[constraintRawPropMapping[constraintKey]],
      constraintRuleTypeMapping[constraintKey]
    );
    const { ruleMap, conditionGroupMap, conditionMap } = getRulesModel(
      rawRules,
      constraintRuleTypeMapping[constraintKey],
      { processId, targetFieldTag: field.tag }
    );

    aggregatedRuleMap = { ...aggregatedRuleMap, ...ruleMap };
    aggregatedConditionGroupMap = {
      ...aggregatedConditionGroupMap,
      ...conditionGroupMap
    };
    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditionMap };

    field[constraintKey] = Object.keys(ruleMap);
  });

  return {
    field,
    ruleMap: aggregatedRuleMap,
    conditionGroupMap: aggregatedConditionGroupMap,
    conditionMap: aggregatedConditionMap
  };
};

const fieldExtraModelCreator = (
  rawFieldExtra: ZodField["extra"]
): Field["extra"] => {
  if (!rawFieldExtra) return rawFieldExtra;

  const reverseParsedExtra = { ...rawFieldExtra };

  if (rawFieldExtra?.font_weight) {
    reverseParsedExtra.font_weight =
      rawFieldExtra.font_weight === "normal" ? "normal" : "bold";
  }

  if (rawFieldExtra?.underline) {
    reverseParsedExtra.underline =
      rawFieldExtra.underline === "true" ? "true" : undefined;
  }

  if (
    rawFieldExtra?.child_mapping &&
    rawFieldExtra?.["l0"]?.datasource?.child_mapping_field_tag
  ) {
    // Backward Compatibility for child_mapping from old to new format
    const oldChildMappingTag =
      rawFieldExtra.l0?.datasource.child_mapping_field_tag;
    reverseParsedExtra.l0.datasource.child_mapping_tag = oldChildMappingTag;

    reverseParsedExtra.childMappingForAllLevels = {
      [oldChildMappingTag]: {
        child_field_mapping:
          rawFieldExtra?.child_mapping.child_field_mapping || {},
        child_workflow_kind: rawFieldExtra.l0.datasource.kind_tag
      }
    };

    delete reverseParsedExtra.l0.datasource.child_mapping_field_tag;
    delete reverseParsedExtra.child_mapping;
  }

  return reverseParsedExtra;
};
